import { inTestMode } from '@shared/constants';
import { isOneOf } from '@shared/helpers/array.helpers';
import { roundToDecimals } from '@shared/helpers/math.helpers';
import { getUrlParam } from '@shared/helpers/url.helpers';
import type { DesignExpressionVariables } from '@shared/models/design/design-expression-variables.model';
import {
  getStackGfa,
  sumStoreysFromDesignExpressionVariables,
} from './storey-sum-from-design.helpers';

/** Set to `true` to print storey/envelope debug tables in the app and in Vitest. */
const DEBUG_STOREY = false;
const DEBUG_PARAMS = ['storey', 'all', 'true'];

const isStoreyDebugEnabled = (): boolean => {
  if (DEBUG_STOREY) return true;
  if (inTestMode()) return false;
  return isOneOf(DEBUG_PARAMS, getUrlParam('debug'));
};

const m = (value: number): number => roundToDecimals(value, 2);

type StoreyDebugRow = {
  Floor: string;
  gfa: number;
  height: number;
  perimeter: number;
  perimeter_inner: number | string;
  gwa: number;
  external_gwa: number;
  gfa_sky: number;
  gfa_terrace_slabs: number;
  gfa_ground_floor_slabs: number;
  gfa_cantilevered_floor_slabs: number;
  covered_gfa_above: number | string;
  covered_gfa_below: number | string;
  is_ground_floor: number | string;
};

/**
 * Prints per-building storey envelope tables when `DEBUG_STOREY` is `true` or
 * the URL has `?debug=storey` (also `all` / `true`). No-op in Vitest unless
 * `DEBUG_STOREY` is on. Call when {@link DesignExpressionVariables} are
 * recomputed so values stay in sync with the design.
 */
export const logStoreyExpressionDebug = (
  designVariables: DesignExpressionVariables,
): void => {
  if (!isStoreyDebugEnabled()) return;

  const site = sumStoreysFromDesignExpressionVariables(designVariables);
  const stacks = designVariables.storeysByBuilding ?? [designVariables.storeys];
  const maxFloors = Math.max(0, ...stacks.map((stack) => stack.length));
  const siteGfa = stacks.reduce((acc, stack) => acc + getStackGfa(stack), 0);

  console.info('==================== STOREY DEBUG ====================');
  console.info(
    'getStorey() = all floors, all buildings. getStorey(n) = floor n of every building (1-based).',
  );

  console.group('Site — getStorey()');
  console.table([
    toSiteRow('getStorey()', site.sum, {
      gwa: designVariables.gwa,
      height: designVariables.total_height,
      perimeter: designVariables.perimeter,
    }),
  ]);
  console.groupEnd();

  if (maxFloors > 0) {
    console.group('Site — getStorey(n) per floor index');
    console.table(
      Array.from({ length: maxFloors }, (_, floor) => {
        const oneBasedFloor = floor + 1;
        const floorSum = sumStoreysFromDesignExpressionVariables(
          designVariables,
          { selectors: [floor] },
        ).sum;
        const designFloor = designVariables.getStorey(oneBasedFloor);
        return toSiteRow(`getStorey(${oneBasedFloor})`, floorSum, {
          gwa: designFloor.gwa,
          height: designFloor.total_height,
          perimeter: designFloor.perimeter,
        });
      }),
    );
    console.groupEnd();
  }

  stacks.forEach((stack, buildingIndex) => {
    const label =
      designVariables.getBuildings(buildingIndex + 1).name ||
      `Building ${buildingIndex + 1}`;
    const buildingSum = sumStoreysFromDesignExpressionVariables(
      {
        ...designVariables,
        storeys: stack,
        storeysByBuilding: undefined,
      },
      { siteGfa },
    );

    console.group(`Building ${buildingIndex + 1} — ${label}`);
    console.table(
      stack.map((storey, floor) => {
        const calculated = buildingSum.storeys[floor];
        return {
          Floor: String(floor),
          gfa: m(storey.gfa),
          height: m(storey.height),
          perimeter: m(storey.perimeter),
          perimeter_inner: m(storey.perimeter_inner ?? 0),
          gwa: m(storey.gwa),
          external_gwa: m(calculated?.external_gwa ?? 0),
          gfa_sky: m(calculated?.gfa_sky ?? 0),
          gfa_terrace_slabs: m(calculated?.gfa_terrace_slabs ?? 0),
          gfa_ground_floor_slabs: m(calculated?.gfa_ground_floor_slabs ?? 0),
          gfa_cantilevered_floor_slabs: m(
            calculated?.gfa_cantilevered_floor_slabs ?? 0,
          ),
          covered_gfa_above: m(storey.covered_gfa_above ?? 0),
          covered_gfa_below: m(storey.covered_gfa_below ?? 0),
          is_ground_floor: storey.is_ground_floor ?? 0,
        } satisfies StoreyDebugRow;
      }),
    );
    console.groupEnd();
  });

  console.info('======================================================');
};

const toSiteRow = (
  floor: string,
  sum: {
    gfa: number;
    total_height: number;
    perimeter: number;
    external_gwa: number;
    gfa_sky: number;
    gfa_terrace_slabs: number;
    gfa_ground_floor_slabs: number;
    gfa_cantilevered_floor_slabs: number;
  },
  design: {
    gwa: number;
    height: number;
    perimeter: number;
  },
): StoreyDebugRow => ({
  Floor: floor,
  gfa: m(sum.gfa),
  height: m(design.height),
  perimeter: m(design.perimeter),
  perimeter_inner: '—',
  gwa: m(design.gwa),
  external_gwa: m(sum.external_gwa),
  gfa_sky: m(sum.gfa_sky),
  gfa_terrace_slabs: m(sum.gfa_terrace_slabs),
  gfa_ground_floor_slabs: m(sum.gfa_ground_floor_slabs),
  gfa_cantilevered_floor_slabs: m(sum.gfa_cantilevered_floor_slabs),
  covered_gfa_above: '—',
  covered_gfa_below: '—',
  is_ground_floor: '—',
});
